import { SalonDocument } from "../../../../types/salonDocument";
import { parseChartDocumentFromVersionMetadata } from "./chartMarkupDocument";

function readApiString(
  raw: Record<string, unknown>,
  camel: string,
  snake: string,
): string | undefined {
  const v = raw[camel] ?? raw[snake];
  return typeof v === "string" ? v : undefined;
}

/** List/detail APIs sometimes return metadata as a JSON string instead of an object. */
function coerceMetadataRecord(value: unknown): Record<string, unknown> | null {
  if (value == null) return null;
  if (typeof value === "string") {
    const s = value.trim();
    if (!s) return null;
    try {
      const parsed = JSON.parse(s) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
    return null;
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function readApiMetadata(
  raw: Record<string, unknown>,
): Record<string, unknown> | null {
  return (
    coerceMetadataRecord(raw.currentMetadata ?? raw.current_metadata) ??
    coerceMetadataRecord(raw.metadata) ??
    coerceMetadataRecord(raw.version_metadata)
  );
}

/**
 * Normalizes a raw `GET .../documents` row (camelCase, snake_case, or stringified
 * `current_metadata`) so title and dates resolve consistently for charts.
 */
export function normalizeSalonDocumentFromListApi(row: unknown): SalonDocument {
  const base = (
    row && typeof row === "object" ? row : {}
  ) as Partial<SalonDocument> & Record<string, unknown>;
  const r = base as Record<string, unknown>;

  const documentKey = String(base.documentKey ?? r.document_key ?? "");
  const documentUuid = String(base.documentUuid ?? r.document_uuid ?? "");
  const currentVersionNumber = Number(
    base.currentVersionNumber ?? r.current_version_number ?? 0,
  );
  const currentUrl = String(base.currentUrl ?? r.current_url ?? "");
  const currentCreatedDt = String(
    base.currentCreatedDt ?? r.current_created_dt ?? "",
  );

  const displayNameRaw =
    (typeof base.displayName === "string" && base.displayName.trim()
      ? base.displayName.trim()
      : undefined) ??
    (typeof r.display_name === "string" ? r.display_name.trim() : undefined) ??
    (typeof r.displayName === "string" ? r.displayName.trim() : undefined);

  const currentMetadata =
    coerceMetadataRecord(
      r.currentMetadata ?? r.current_metadata ?? r.metadata,
    ) ?? coerceMetadataRecord(base.currentMetadata);

  return {
    ...(base as SalonDocument),
    documentKey,
    documentUuid,
    currentVersionNumber,
    currentUrl,
    currentCreatedDt,
    displayName: displayNameRaw ?? base.displayName ?? null,
    currentMetadata: currentMetadata ?? base.currentMetadata ?? null,
  };
}

/**
 * Human-readable chart title for list cards from GET /documents list rows.
 * Call with {@link normalizeSalonDocumentFromListApi} first when reading list payloads.
 */
export function getChartListTitle(
  doc: SalonDocument,
  fallbackLabel: string,
): string {
  const raw = doc as unknown as Record<string, unknown>;

  const fromDisplay = readApiString(raw, "displayName", "display_name")?.trim();
  if (fromDisplay) return fromDisplay;

  const fromAltDisplay = readApiString(
    raw,
    "documentDisplayName",
    "document_display_name",
  )?.trim();
  if (fromAltDisplay) return fromAltDisplay;

  if (typeof raw.title === "string" && raw.title.trim()) {
    return raw.title.trim();
  }

  const meta = readApiMetadata(raw);
  if (meta) {
    const chart = parseChartDocumentFromVersionMetadata(meta);
    const t = chart?.metadata?.title?.trim();
    if (t) return t;
  }

  const flatTitle = meta?.title;
  if (typeof flatTitle === "string" && flatTitle.trim())
    return flatTitle.trim();

  return fallbackLabel;
}

function unwrapDetailPayload(detail: unknown): Record<string, unknown> | null {
  if (!detail || typeof detail !== "object") return null;
  const o = detail as Record<string, unknown>;
  const inner = o.data;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    return inner as Record<string, unknown>;
  }
  return o;
}

interface VersionLike {
  versionNumber: number;
  metadata?: unknown;
  displayName?: string | null;
  display_name?: string | null;
}

/**
 * Reads a human title from `GET .../documents/{documentKey}` when the **list**
 * response omits `displayName` / `currentMetadata` (common for SSG list rows).
 */
export function extractChartTitleFromDocumentDetailResponse(
  detail: unknown,
): string | null {
  const r = unwrapDetailPayload(detail);
  if (!r) return null;

  const fromRoot = readApiString(r, "displayName", "display_name")?.trim();
  if (fromRoot) return fromRoot;

  const rawVersions = r.versions ?? r.version_list;
  if (!Array.isArray(rawVersions) || rawVersions.length === 0) return null;

  const versions = rawVersions as VersionLike[];
  const current = versions.reduce((best, v) =>
    v.versionNumber > best.versionNumber ? v : best,
  );

  const vRec = current as unknown as Record<string, unknown>;
  const fromVersion = readApiString(
    vRec,
    "displayName",
    "display_name",
  )?.trim();
  if (fromVersion) return fromVersion;

  const meta = coerceMetadataRecord(current.metadata);
  if (meta) {
    const chart = parseChartDocumentFromVersionMetadata(meta);
    const t = chart?.metadata?.title?.trim();
    if (t) return t;
    const flat = meta.title;
    if (typeof flat === "string" && flat.trim()) return flat.trim();
  }

  return null;
}
